/**
 * +castux: range / line-of-sight dimming for the target frame, nameplates and party frames.
 *
 * A unit is "dim" when it is out of range of the player's main ability for it (the first hotbar
 * ability that can target it: an enemy spell for hostiles, an ally / any spell for friends; melee
 * abilities use melee range) or out of line of sight (walls between, sampled on the sim's
 * walkability like Tab targeting does: input/targeting.ts walkableLos).
 *
 * Note: the sim has no line-of-sight rule for casts (no CastFailReason for it), so LoS here is
 * information only; casts through walls still go off. `dimDecision` is pure (unit-tested).
 */
import type { AbilityDef, ClassDef, ContentRegistry, DeepReadonly, SimQueries, UnitId, Vec2, World } from '@keystone/sim';
import { UI_BIND_KEYS, abilityForBind } from '../input/bindings';
import { walkableLos } from '../input/targeting';
import { FRAME_RANGE } from './frames/model';

export interface RangeInfo {
  inRange: boolean;
  los: boolean;
}

/** Dim when out of range or out of line of sight. */
export function dimDecision(r: RangeInfo): boolean {
  return !r.inRange || !r.los;
}

type Ab = Pick<DeepReadonly<AbilityDef>, 'id' | 'targeting' | 'range' | 'melee'>;

/** The class's main ability for a hostile (enemy-targeted) or friendly (ally / any) unit, by hotbar order. */
export function mainAbilityFor(cls: Pick<ClassDef, 'binds' | 'extraAbilities' | 'extraBinds'> | undefined, ability: (id: string) => Ab | undefined, hostile: boolean): Ab | null {
  if (!cls) return null;
  for (const bind of UI_BIND_KEYS) {
    const id = abilityForBind(cls, bind);
    const a = id ? ability(id) : undefined;
    if (!a) continue;
    if (hostile ? a.targeting === 'enemy' || a.targeting === 'any' : a.targeting === 'ally' || a.targeting === 'any') return a;
  }
  return null;
}

/** Re-check each unit at most this often (ms, real time). */
const RECHECK_MS = 150;

interface Cached {
  at: number;
  dim: boolean;
}

/**
 * Per-frame helper owned by the HUD: `begin` with the frame's state, then `dim(id)` per unit
 * (cached for RECHECK_MS; the cache entries are reused, nothing is allocated per frame).
 */
export class RangeChecker {
  private world: DeepReadonly<World> | null = null;
  private queries: SimQueries | null = null;
  private content: ContentRegistry | null = null;
  private playerId: UnitId | null = null;
  private now = 0;
  private readonly cache = new Map<UnitId, Cached>();
  private readonly mainOf = new Map<string, Ab | null>();
  private readonly los: (a: Vec2, b: Vec2) => boolean;
  private readonly info: RangeInfo = { inRange: true, los: true };

  constructor() {
    this.los = walkableLos((p) => {
      try {
        return !this.queries || !this.world || this.queries.spatial.isWalkable(this.world as World, p);
      } catch {
        return true;
      }
    });
  }

  begin(world: DeepReadonly<World>, queries: SimQueries, content: ContentRegistry, playerId: UnitId | null, realNow: number): void {
    if (content !== this.content) this.mainOf.clear();
    if (playerId !== this.playerId) this.cache.clear();
    this.world = world;
    this.queries = queries;
    this.content = content;
    this.playerId = playerId;
    this.now = realNow;
    if (this.cache.size > 64) this.cache.clear();
  }

  /** Should `id`'s frame / plate be dimmed (out of range of the main ability, or out of LoS)? */
  dim(id: UnitId | null): boolean {
    if (!id || !this.world || !this.queries || !this.content || !this.playerId || id === this.playerId) return false;
    let c = this.cache.get(id);
    if (c && this.now - c.at < RECHECK_MS && this.now >= c.at) return c.dim;
    if (!c) this.cache.set(id, (c = { at: 0, dim: false }));
    c.at = this.now;
    c.dim = this.compute(id);
    return c.dim;
  }

  /** Line of sight only (party frames: range is their own 40 yd rule). */
  inSight(id: UnitId): boolean {
    const w = this.world;
    const me = w && this.playerId ? w.units[this.playerId] : undefined;
    const u = w?.units[id];
    return !me || !u || this.los(me.pos, u.pos);
  }

  private compute(id: UnitId): boolean {
    const w = this.world!;
    const me = w.units[this.playerId!];
    const u = w.units[id];
    if (!me || !u || !me.alive || !u.alive) return false;
    const hostile = u.faction !== me.faction;
    const key = `${me.classId ?? ''}|${hostile ? 'h' : 'f'}`;
    let main = this.mainOf.get(key);
    if (main === undefined) {
      const content = this.content!;
      main = mainAbilityFor(me.classId ? content.class(me.classId) : undefined, (aid) => content.ability(aid), hostile);
      this.mainOf.set(key, main);
    }
    const info = this.info;
    try {
      const q = this.queries!.spatial;
      if (!main) info.inRange = q.rangeBetween(w as World, me.id, id) <= FRAME_RANGE;
      else if (main.melee || (main.targeting === 'enemy' && !main.range)) info.inRange = q.inMeleeRange(w as World, me.id, id);
      else info.inRange = q.inRange(w as World, me.id, id, main.range ?? FRAME_RANGE);
    } catch {
      info.inRange = true;
    }
    info.los = this.los(me.pos, u.pos);
    return dimDecision(info);
  }
}
